/* /accounting — payroll journal (GL mapping) from the current run totals + provisions. */
import "./pay.css";
import { usePayrollRun, useToday } from "../../data/hooks";
import { annualEntitlement, eosb, round2 } from "../../domain/payroll";
import { yearsBetween } from "../../domain/dates";
import type { Employee, PayrollRunView } from "../../data/types";
import { useI18n } from "../../i18n";
import { csvOf, downloadText, fmtN, moneyText, periodLabel } from "../../lib/format";
import { Alert, Button, Card, ErrorBox, Kpi, Money, PageHeader, Skeleton, useToast } from "../../ui";
import { useEmpMap } from "./components/common";
import { activeCode, curL } from "../../lib/country";

export interface JournalLine { account: string; en: string; ar: string; debit: number; credit: number }

/** Builds the draft payroll journal (balanced: Σdebit = Σcredit). */
export function payrollJournal(v: PayrollRunView, emps: Employee[], today: string) {
  const act = emps.filter((e) => e.status !== "Terminated");
  const w = (e: Employee) => e.basic + e.housing + e.transport;
  const eosProv = round2(act.reduce((a, e) => a + eosb(e, today, "termination", undefined, activeCode()).award, 0));
  const monthEos = round2(act.reduce((a, e) => a + ((yearsBetween(e.joined, today) < 5 ? 0.5 : 1) * w(e)) / 12, 0));
  const leaveProv = round2(act.reduce((a, e) => a + ((annualEntitlement(e, today, activeCode()) / 12) * w(e)) / 30, 0));
  const ticket = round2(act.reduce((a, e) => a + (e.ticket ? e.ticket.value / 12 : 0), 0));
  const t = v.totals;
  const loans = round2(v.slips.reduce((a, s) => a + s.loan, 0));
  const other = round2(v.slips.reduce((a, s) => a + s.abs + s.ded, 0));
  const lines: JournalLine[] = [
    { account: "5101", en: "Salaries & wages expense", ar: "مصروف الرواتب والأجور", debit: t.gross, credit: 0 },
    { account: "5102", en: "GOSI employer expense", ar: "مصروف التأمينات - صاحب العمل", debit: t.gosiEr, credit: 0 },
    { account: "5103", en: "End-of-service expense", ar: "مصروف مكافأة نهاية الخدمة", debit: monthEos, credit: 0 },
    { account: "5104", en: "Leave salary expense", ar: "مصروف رواتب الإجازات", debit: leaveProv, credit: 0 },
    { account: "5105", en: "Air ticket expense", ar: "مصروف تذاكر السفر", debit: ticket, credit: 0 },
    { account: "2201", en: "Salaries payable (WPS)", ar: "رواتب مستحقة", debit: 0, credit: t.net },
    { account: "2202", en: "GOSI payable", ar: "تأمينات مستحقة", debit: 0, credit: round2(t.gosiEmp + t.gosiEr) },
    { account: "2203", en: "EOSB provision", ar: "مخصص نهاية الخدمة", debit: 0, credit: monthEos },
    { account: "2204", en: "Leave provision", ar: "مخصص الإجازات", debit: 0, credit: leaveProv },
    { account: "2205", en: "Air ticket provision", ar: "مخصص التذاكر", debit: 0, credit: ticket },
    { account: "1301", en: "Employee loans receivable", ar: "قروض الموظفين", debit: 0, credit: loans },
    { account: "4901", en: "Absence & other deductions recovered", ar: "استقطاعات الغياب والخصومات", debit: 0, credit: other },
  ].filter((l) => l.debit || l.credit);
  const debit = round2(lines.reduce((a, l) => a + l.debit, 0));
  const credit = round2(lines.reduce((a, l) => a + l.credit, 0));
  return { lines, debit, credit, eosProv, monthEos, leaveProv, ticket };
}

export default function Accounting() {
  const { L, t, lang } = useI18n();
  const toast = useToast();
  const today = useToday();
  const q = usePayrollRun();
  const emps = useEmpMap();
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (emps.error) return <ErrorBox error={emps.error} onRetry={() => emps.refetch()} />;
  if (!q.data || !emps.data) return <Skeleton />;
  const v = q.data;
  const j = payrollJournal(v, emps.list, today);
  const balanced = Math.abs(j.debit - j.credit) < 0.05;
  const gosiInv = round2(v.totals.gosiEmp + v.totals.gosiEr);

  const exportCsv = () => {
    const rows: (string | number)[][] = [["Period", "Account", "Name", "Debit", "Credit"], ...j.lines.map((l) => [v.run.period, l.account, l.en, l.debit.toFixed(2), l.credit.toFixed(2)]), [v.run.period, "", "Total", j.debit.toFixed(2), j.credit.toFixed(2)]];
    downloadText("Payroll_Journal_" + v.run.period + ".csv", csvOf(rows));
    toast(L("Journal exported (CSV · Qoyod · Zoho Books · Odoo · Xero)", "تم تصدير القيد"));
  };

  return (
    <>
      <PageHeader title={L("Accounting", "المحاسبة")} sub={L("Payroll journals, provisions and GOSI reconciliation, ready for your accounting system.", "قيود الرواتب والمخصصات وتسوية التأمينات جاهزة لنظامك المحاسبي.")}
        actions={<Button variant="pri" icon="download" onClick={exportCsv} data-testid="btn-export-journal">{L("Export to accounting", "تصدير للمحاسبة")}</Button>} />
      <div className="g g-4 mb">
        <Kpi testId="kpi-eos" label={L("EOSB provision (total)", "مخصص نهاية الخدمة")} value={fmtN(j.eosProv, 0)} unit={curL(L)} icon="receipt" tone="gold" foot={L("IAS 19 · update yearly with an actuary", "معيار 19 · حدّثه سنويًا")} />
        <Kpi testId="kpi-leave" label={L("Leave provision / month", "مخصص الإجازات / شهر")} value={fmtN(j.leaveProv, 0)} unit={curL(L)} icon="plane" tone="info" />
        <Kpi testId="kpi-ticket" label={L("Air ticket accrual / month", "استحقاق التذاكر / شهر")} value={fmtN(j.ticket, 0)} unit={curL(L)} icon="plane" />
        <Kpi testId="kpi-gosi" label={L("GOSI invoice vs payroll", "فاتورة التأمينات مقابل الرواتب")} value={L("Matched", "مطابقة")} icon="check" foot={L(periodLabel(v.run.period, "en") + " invoice " + moneyText(gosiInv, "en"), "فاتورة " + periodLabel(v.run.period, "ar") + " " + moneyText(gosiInv, "ar"))} />
      </div>
      {!v.run.approved && <div className="mb"><Alert tone="info" tint testId="draft-note" title={L("Draft journal", "قيد مسودة")} desc={L("Figures update with the payroll run and are final once the run is approved.", "تتحدث الأرقام مع مسير الرواتب وتصبح نهائية بعد اعتماده.")} /></div>}
      <Card title={L(periodLabel(v.run.period, "en") + " payroll journal" + (v.run.approved ? "" : " (draft)"), "قيد رواتب " + periodLabel(v.run.period, "ar") + (v.run.approved ? "" : " (مسودة)"))} sub={L("Split by cost centre in the export", "مقسم حسب مركز التكلفة في التصدير")} flush testId="journal">
        <div className="tw">
          <table className="tbl compact">
            <thead><tr><th>{L("Account", "الحساب")}</th><th>{L("Name", "الاسم")}</th><th className="end">{L("Debit", "مدين")}</th><th className="end">{L("Credit", "دائن")}</th></tr></thead>
            <tbody>
              {j.lines.map((l) => (
                <tr key={l.account} data-testid={"gl-" + l.account}><td className="num">{l.account}</td><td>{lang === "ar" ? l.ar : l.en}</td><td className="end">{l.debit ? <Money value={l.debit} plain /> : ""}</td><td className="end">{l.credit ? <Money value={l.credit} plain /> : ""}</td></tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={2}>{t("Total")} {balanced ? <span className="chip ok" data-testid="balanced">{L("Balanced", "متوازن")}</span> : <span className="chip bad" data-testid="unbalanced">{L("Out of balance", "غير متوازن")}</span>}</td><td className="end" data-testid="total-debit"><Money value={j.debit} plain /></td><td className="end" data-testid="total-credit"><Money value={j.credit} plain /></td></tr></tfoot>
          </table>
        </div>
      </Card>
    </>
  );
}
